/**
 * PMMyProjects - Display PM's assigned projects
 *
 * Shows projects assigned to the current PM with:
 * - Lifecycle status (In Intake, WO Draft, WO Published, etc.)
 * - Actions: Generate WO, Mark PDF Ready
 * - SKU list with artwork tracking status
 */

import { useState, useEffect, useCallback, useRef } from "react";
import SharePointFolderModal from "./SharePointFolderModal";

// PM Readiness status labels and colors
const PM_READINESS_CONFIG = {
  IN_INTAKE: {
    label: "In Intake",
    color: "bg-blue-100 text-blue-700",
    description: "New project, needs work order",
  },
  WO_DRAFT: {
    label: "WO Draft",
    color: "bg-yellow-100 text-yellow-700",
    description: "Work order being drafted",
  },
  WO_PUBLISHED: {
    label: "WO Published",
    color: "bg-green-100 text-green-700",
    description: "Work order sent to production",
  },
  READY_FOR_BILLING: {
    label: "Ready for Billing",
    color: "bg-purple-100 text-purple-700",
    description: "Production complete, ready to close",
  },
};

export default function PMMyProjects({ acquireAccessToken, lifecycleFilter }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // SKU tracking state
  const [skuTracking, setSkuTracking] = useState([]);
  const [skuTrackingSummary, setSkuTrackingSummary] = useState({});
  const [updatingSku, setUpdatingSku] = useState(null);

  // Track which quote number the current data is for (to prevent race conditions)
  const currentQuoteRef = useRef(null);

  // SharePoint folder modal state
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [pendingWOProject, setPendingWOProject] = useState(null);
  const [generatingWO, setGeneratingWO] = useState(false);
  const abortControllerRef = useRef(null);

  // Fundamentals checklist state
  const [fundamentals, setFundamentals] = useState({
    kickoff_received: false,
    folder_created: false,
    artwork_received: false,
  });

  // Fetch assigned projects
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) {
        setError("Authentication required");
        return;
      }

      // Build URL with optional lifecycle filter
      const params = new URLSearchParams();
      if (lifecycleFilter) {
        params.set("pmReadiness", lifecycleFilter);
      }
      const url = `/api/pm-center/projects/my-projects${params.toString() ? `?${params}` : ""}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch projects");
      }

      const data = await res.json();
      setProjects(data.projects || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching my projects:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [acquireAccessToken, lifecycleFilter]);

  // Fetch project details and SKU tracking for a specific quote
  const fetchProjectData = useCallback(async (quoteNumber) => {
    // Cancel any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Track which quote we're fetching for
    currentQuoteRef.current = quoteNumber;

    // Clear previous data immediately
    setProjectDetails(null);
    setSkuTracking([]);
    setSkuTrackingSummary({});
    setFundamentals({ kickoff_received: false, folder_created: false, artwork_received: false });
    setLoadingDetails(true);

    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      // Fetch all in parallel
      const [detailsRes, trackingRes, fundamentalsRes] = await Promise.all([
        fetch(`/api/pm-center/pipeline/v2/project/${quoteNumber}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: abortController.signal,
        }),
        fetch(`/api/pm-center/projects/${quoteNumber}/sku-tracking`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: abortController.signal,
        }),
        fetch(`/api/pm-center/projects/${quoteNumber}/fundamentals`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: abortController.signal,
        }),
      ]);

      // Check if we're still on the same project (user didn't switch)
      if (currentQuoteRef.current !== quoteNumber) {
        return;
      }

      // Process project details
      if (detailsRes.ok) {
        const detailsData = await detailsRes.json();
        if (currentQuoteRef.current === quoteNumber) {
          setProjectDetails(detailsData);
        }
      }

      // Process SKU tracking
      if (trackingRes.ok) {
        const trackingData = await trackingRes.json();
        if (currentQuoteRef.current === quoteNumber) {
          setSkuTracking(trackingData.skuTracking || []);
          setSkuTrackingSummary(trackingData.summary || {});
        }
      }

      // Process fundamentals
      if (fundamentalsRes.ok) {
        const fundamentalsData = await fundamentalsRes.json();
        if (currentQuoteRef.current === quoteNumber) {
          setFundamentals(fundamentalsData.fundamentals || {
            kickoff_received: false,
            folder_created: false,
            artwork_received: false,
          });
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }
      console.error("Error fetching project data:", err);
    } finally {
      if (currentQuoteRef.current === quoteNumber) {
        setLoadingDetails(false);
      }
    }
  }, [acquireAccessToken]);

  // Update a single SKU's tracking status
  const updateSkuStatus = useCallback(async (sku, field, value) => {
    if (!selectedProject) return;

    const quoteNumber = selectedProject.quote_number;

    try {
      setUpdatingSku(sku);
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch(
        `/api/pm-center/projects/${quoteNumber}/sku-tracking/${encodeURIComponent(sku)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ [field]: value }),
        }
      );

      if (res.ok) {
        const data = await res.json();
        // Only update if still on the same project
        if (currentQuoteRef.current === quoteNumber) {
          // Update the specific SKU in tracking array
          setSkuTracking((prev) => {
            const existingIndex = prev.findIndex((s) => s.sku === sku);
            if (existingIndex >= 0) {
              // Update existing entry
              const updated = [...prev];
              updated[existingIndex] = { ...updated[existingIndex], ...data.sku };
              return updated;
            } else {
              // Add new entry
              return [...prev, data.sku];
            }
          });
          setSkuTrackingSummary(data.summary);
        }
      } else {
        const err = await res.json();
        alert(`Error: ${err.error}`);
      }
    } catch (err) {
      console.error("Error updating SKU:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setUpdatingSku(null);
    }
  }, [selectedProject, acquireAccessToken]);

  // Initialize SKU tracking from project details SKUs
  const initializeSkuTracking = useCallback(async (quoteNumber, skus) => {
    if (!quoteNumber || !skus?.length) return;

    const accessToken = await acquireAccessToken();
    if (!accessToken) return;

    // Get existing tracking
    const existingRes = await fetch(
      `/api/pm-center/projects/${quoteNumber}/sku-tracking`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    let existing = [];
    if (existingRes.ok) {
      const data = await existingRes.json();
      existing = data.skuTracking || [];
    }

    // Map project SKUs to tracking entries
    const existingSkuSet = new Set(existing.map((s) => s.sku));

    const newTracking = skus
      .filter((item) => item.sku && !existingSkuSet.has(item.sku))
      .map((item) => ({
        sku: item.sku,
        description: item.description,
        productType: item.productType || "",
        quantity: item.quantity || 0,
        artwork: "missing",
        structure: "not_needed",
        poaCount: 1,
      }));

    if (newTracking.length > 0) {
      // Save the combined tracking
      const combined = [...existing, ...newTracking];
      const res = await fetch(
        `/api/pm-center/projects/${quoteNumber}/sku-tracking`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ skuTracking: combined }),
        }
      );

      if (res.ok && currentQuoteRef.current === quoteNumber) {
        const data = await res.json();
        setSkuTracking(data.skuTracking);
        setSkuTrackingSummary(data.summary);
      }
    } else if (currentQuoteRef.current === quoteNumber) {
      setSkuTracking(existing);
    }
  }, [acquireAccessToken]);

  // Load projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Load details when a project is selected
  useEffect(() => {
    if (selectedProject) {
      fetchProjectData(selectedProject.quote_number);
    } else {
      currentQuoteRef.current = null;
      setProjectDetails(null);
      setSkuTracking([]);
      setSkuTrackingSummary({});
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedProject, fetchProjectData]);

  // Initialize SKU tracking when project details are loaded
  useEffect(() => {
    if (
      projectDetails?.skus?.length > 0 &&
      skuTracking.length === 0 &&
      projectDetails.quoteNumber === currentQuoteRef.current
    ) {
      initializeSkuTracking(projectDetails.quoteNumber, projectDetails.skus);
    }
  }, [projectDetails, skuTracking.length, initializeSkuTracking]);

  // Generate Work Order - Step 1: Show folder modal
  const handleGenerateWO = useCallback((project) => {
    if (!project.jobboss_order_number) {
      alert("Cannot generate work order without an order number. Wait for the order to be created in JobBoss.");
      return;
    }
    setPendingWOProject(project);
    setShowFolderModal(true);
  }, []);

  // Generate Work Order - Step 2: Actually generate after folder selected
  const handleConfirmGenerateWO = useCallback(async (folderUrl) => {
    if (!pendingWOProject) return;

    try {
      setGeneratingWO(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch("/api/pm-center/work-orders/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          orderNumber: pendingWOProject.jobboss_order_number,
          quoteNumber: pendingWOProject.quote_number,
          uploadFolder: folderUrl,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to generate work order");
      }

      const result = await res.json();

      // Copy SharePoint URL to clipboard
      if (result.sharepointUrl) {
        await navigator.clipboard.writeText(result.sharepointUrl);
      }

      setShowFolderModal(false);
      setPendingWOProject(null);

      alert(`Work Order generated: ${result.fileName}\n\nSharePoint URL copied to clipboard.`);

      fetchProjects();
    } catch (err) {
      console.error("Error generating work order:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setGeneratingWO(false);
    }
  }, [acquireAccessToken, fetchProjects, pendingWOProject]);

  // Mark PDF Ready action
  const handleMarkPDFReady = useCallback(async (project) => {
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const confirmed = confirm(
        `Mark PDF as ready for customer approval?\n\n` +
        `Project: ${project.quote_number} - ${project.customer_name}\n` +
        `This will notify the sales rep.`
      );
      if (!confirmed) return;

      const res = await fetch(`/api/pm-center/projects/${project.quote_number}/pdf-ready`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ notes: "" }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to mark PDF ready");
      }

      const result = await res.json();

      if (result.alreadyMarked) {
        alert(`PDF was already marked ready within the last 24 hours.\n\nLast marked: ${new Date(result.lastMarkedAt).toLocaleString()}`);
      } else {
        alert(`PDF marked as ready!\n\nSales rep has been notified.`);
      }
    } catch (err) {
      console.error("Error marking PDF ready:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken]);

  // Unassign project - return to Pipeline
  const handleUnassign = useCallback(async (project) => {
    const confirmed = confirm(
      `Unassign this project?\n\n` +
      `Project: ${project.quote_number} - ${project.customer_name}\n\n` +
      `This will return the project to the Pipeline for another PM to pick up.`
    );
    if (!confirmed) return;

    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch(
        `/api/pm-center/projects/${encodeURIComponent(project.quote_number)}/unassign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to unassign project");
      }

      // Clear selection and refresh list
      setSelectedProject(null);
      fetchProjects();
    } catch (err) {
      console.error("Error unassigning project:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken, fetchProjects]);

  // Send back to sales - missing fundamentals
  const handleSendBackToSales = useCallback(async (project) => {
    // Show options for what's missing
    const options = [
      "Folder not created on server",
      "Artwork not saved on server",
      "Structures not available",
      "Project not marked as WON in JobBoss",
      "Need clarification from customer",
      "Other (specify in notes)",
    ];

    const selection = prompt(
      `Send ${project.quote_number} back to Sales?\n\n` +
      `Select what's missing:\n` +
      options.map((o, i) => `${i + 1}. ${o}`).join("\n") +
      `\n\nEnter number (1-${options.length}):`
    );

    if (!selection) return;

    const selectedIndex = parseInt(selection) - 1;
    if (isNaN(selectedIndex) || selectedIndex < 0 || selectedIndex >= options.length) {
      alert("Invalid selection");
      return;
    }

    let reason = options[selectedIndex];

    // If "Other", prompt for details
    if (selectedIndex === options.length - 1) {
      const customReason = prompt("Enter the reason:");
      if (!customReason) return;
      reason = customReason;
    }

    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch(`/api/pm-center/projects/${project.quote_number}/send-to-sales`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          reason,
          salesRep: project.sales_rep,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to send to sales");
      }

      alert(`Project ${project.quote_number} sent back to Sales.\n\nReason: ${reason}\n\nSales rep has been notified.`);

      // Clear selection and refresh
      setSelectedProject(null);
      fetchProjects();
    } catch (err) {
      console.error("Error sending to sales:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken, fetchProjects]);

  // Update fundamentals checklist
  const updateFundamental = useCallback(async (field, value) => {
    if (!selectedProject) return;

    // Optimistic update
    setFundamentals(prev => ({ ...prev, [field]: value }));

    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch(`/api/pm-center/projects/${selectedProject.quote_number}/fundamentals`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ [field]: value }),
      });

      if (!res.ok) {
        // Revert on error
        setFundamentals(prev => ({ ...prev, [field]: !value }));
        const error = await res.json();
        console.error("Failed to update fundamental:", error);
      }
    } catch (err) {
      // Revert on error
      setFundamentals(prev => ({ ...prev, [field]: !value }));
      console.error("Error updating fundamental:", err);
    }
  }, [acquireAccessToken, selectedProject]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Get display data for SKUs - merge tracking data with project details
  const getSkuDisplayData = useCallback(() => {
    // Build a map of project detail SKUs for enrichment
    const detailsMap = new Map();
    if (projectDetails?.skus) {
      projectDetails.skus.forEach((s) => {
        detailsMap.set(s.sku, s);
      });
    }

    if (skuTracking.length > 0) {
      // Use tracking data, enriched with project details
      return skuTracking.map((tracked) => {
        const detailItem = detailsMap.get(tracked.sku);
        return {
          ...tracked,
          description: tracked.description || detailItem?.description || "",
          productType: tracked.productType || detailItem?.productType || "",
          quantity: tracked.quantity || detailItem?.quantity || 0,
          poaCount: tracked.poaCount ?? 1,
          artwork: tracked.artwork || "missing",
          structure: tracked.structure || "not_needed",
        };
      });
    } else if (projectDetails?.skus?.length > 0) {
      // Fall back to project details SKUs
      return projectDetails.skus.map((s) => ({
        sku: s.sku,
        description: s.description || "",
        productType: s.productType || "",
        quantity: s.quantity || 0,
        artwork: "missing",
        structure: "not_needed",
        poaCount: 1,
      }));
    }
    return [];
  }, [skuTracking, projectDetails]);

  // Determine if a project has been "worked on" (has any SKU tracking data with changes)
  const hasBeenWorkedOn = useCallback((project) => {
    // A project is considered "worked on" if it has any tracking data saved
    // For now, we'll use a simple heuristic based on whether artwork has been updated
    // This could be enhanced with a database flag later
    return project.has_sku_tracking || false;
  }, []);

  // Split projects into sections
  const newlyAssignedProjects = projects.filter((p) => !hasBeenWorkedOn(p));
  const inProgressProjects = projects.filter((p) => hasBeenWorkedOn(p));

  // Empty state
  if (!loading && projects.length === 0) {
    return (
      <div className="flex-1 p-4">
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-4">📋</div>
          <h3 className="text-lg font-medium mb-2 text-gray-600">No Projects Assigned</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            You don't have any projects assigned yet. Use the Pipeline tab to assign projects to yourself.
          </p>
        </div>
      </div>
    );
  }

  const skuDisplayData = selectedProject ? getSkuDisplayData() : [];

  // Project list item component
  const ProjectListItem = ({ project }) => {
    const readinessConfig = PM_READINESS_CONFIG[project.pm_readiness] || {
      label: project.pm_readiness,
      color: "bg-gray-100 text-gray-700",
    };

    return (
      <div
        onClick={() => setSelectedProject(project)}
        className={`p-2 cursor-pointer hover:bg-gray-50 transition-colors ${
          selectedProject?.quote_number === project.quote_number
            ? "bg-purple-50 border-l-4 border-purple-500"
            : ""
        }`}
      >
        <div className="flex items-start justify-between gap-1">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="font-medium text-gray-900 text-sm truncate">
                {project.quote_number}
              </span>
              {project.is_rush && (
                <span className="px-1 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                  RUSH
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 truncate">
              {project.customer_name}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xs text-gray-500">
              {formatDate(project.ship_date)}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Project List - 25% width */}
      <div className="w-1/4 border-r border-gray-200 overflow-y-auto bg-white flex flex-col">
        <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700 text-sm">
              My Projects ({projects.length})
            </h3>
            <button
              onClick={fetchProjects}
              className="text-xs text-gray-500 hover:text-gray-700"
              title="Refresh"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            Loading...
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-500 text-sm">
            {error}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Newly Assigned Section */}
            {newlyAssignedProjects.length > 0 && (
              <div>
                <div className="px-2 py-1 bg-blue-50 border-b border-blue-100 sticky top-0">
                  <h4 className="text-xs font-medium text-blue-700 uppercase tracking-wide">
                    Newly Assigned ({newlyAssignedProjects.length})
                  </h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {newlyAssignedProjects.map((project) => (
                    <ProjectListItem key={project.quote_number} project={project} />
                  ))}
                </div>
              </div>
            )}

            {/* In Progress Section */}
            {inProgressProjects.length > 0 && (
              <div>
                <div className="px-2 py-1 bg-yellow-50 border-b border-yellow-100 sticky top-0">
                  <h4 className="text-xs font-medium text-yellow-700 uppercase tracking-wide">
                    In Progress ({inProgressProjects.length})
                  </h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {inProgressProjects.map((project) => (
                    <ProjectListItem key={project.quote_number} project={project} />
                  ))}
                </div>
              </div>
            )}

            {/* If all projects are in one category, show them without sections */}
            {newlyAssignedProjects.length === 0 && inProgressProjects.length === 0 && projects.length > 0 && (
              <div className="divide-y divide-gray-100">
                {projects.map((project) => (
                  <ProjectListItem key={project.quote_number} project={project} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail Panel - 75% width */}
      <div className="w-3/4 overflow-y-auto bg-gray-50">
        {!selectedProject ? (
          <div className="h-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>Select a project to view details</p>
            </div>
          </div>
        ) : (
          <div className="p-4">
            {/* Project Header */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedProject.quote_number}
                  </h2>
                  <p className="text-gray-600">{selectedProject.customer_name}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedProject.description || "No description"}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-3 py-1 text-sm rounded-full ${
                    PM_READINESS_CONFIG[selectedProject.pm_readiness]?.color || "bg-gray-100 text-gray-700"
                  }`}>
                    {PM_READINESS_CONFIG[selectedProject.pm_readiness]?.label || selectedProject.pm_readiness}
                  </span>
                </div>
              </div>

              {/* Key Info */}
              <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Ship Date</p>
                  <p className="font-medium">{formatDate(selectedProject.ship_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Order #</p>
                  <p className="font-medium">{selectedProject.jobboss_order_number || "Not created"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Assigned</p>
                  <p className="font-medium">{formatDate(selectedProject.assigned_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Status</p>
                  <p className="font-medium">
                    {selectedProject.production_status_cache || "Unknown"}
                  </p>
                </div>
              </div>

              {/* Blocker Alert */}
              {selectedProject.current_blocker && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800">Blocker</p>
                  <p className="text-sm text-red-700">{selectedProject.current_blocker}</p>
                </div>
              )}
            </div>

            {/* Fundamentals Checklist */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-700">Fundamentals Checklist</h3>
                <span className="text-xs text-gray-400">Verify before proceeding</span>
              </div>

              <div className="space-y-2 mb-4">
                {/* Kickoff Email - Primary gate */}
                <label className="flex items-center gap-3 p-2 rounded hover:bg-purple-50 cursor-pointer border border-purple-200 bg-purple-50/50">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-purple-600 rounded border-purple-300 focus:ring-purple-500"
                    checked={fundamentals.kickoff_received}
                    onChange={(e) => updateFundamental('kickoff_received', e.target.checked)}
                  />
                  <div>
                    <span className="text-sm font-medium text-purple-900">Kickoff Email Received</span>
                    <p className="text-xs text-purple-600">Sales has sent the kickoff email</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    checked={fundamentals.folder_created}
                    onChange={(e) => updateFundamental('folder_created', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Folder Created on Server</span>
                </label>

                <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    checked={fundamentals.artwork_received}
                    onChange={(e) => updateFundamental('artwork_received', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Artwork Received</span>
                </label>
              </div>

              {/* Send Back to Sales */}
              <div className="pt-3 border-t border-gray-100">
                <button
                  onClick={() => handleSendBackToSales(selectedProject)}
                  className="w-full px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors text-sm font-medium"
                >
                  Send Back to Sales asking for...
                </button>
              </div>
            </div>

            {/* Primary Action: Generate Work Order */}
            {selectedProject.jobboss_order_number && !selectedProject.has_work_order && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-purple-900">Ready to Generate Work Order</h3>
                    <p className="text-sm text-purple-700 mt-1">
                      {skuTrackingSummary.allArtworkReceived
                        ? "All artwork received. Ready to proceed."
                        : "Review SKU tracking below, then generate WO."}
                    </p>
                  </div>
                  <button
                    onClick={() => handleGenerateWO(selectedProject)}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold shadow-sm"
                  >
                    Generate Work Order
                  </button>
                </div>
              </div>
            )}

            {/* Secondary Actions */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <h3 className="font-medium text-gray-700 mb-3">Actions</h3>
              <div className="flex flex-wrap gap-2">
                {selectedProject.has_work_order && (
                  <button
                    onClick={() => handleMarkPDFReady(selectedProject)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    Mark PDF Ready
                  </button>
                )}

                {!selectedProject.jobboss_order_number && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="italic">Waiting for order to be created in JobBoss...</span>
                  </div>
                )}

                {selectedProject.has_work_order && (
                  <span className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm">
                    Work Order Generated
                  </span>
                )}

                {/* Unassign - only show for IN_INTAKE or WO_DRAFT states */}
                {(selectedProject.pm_readiness === 'IN_INTAKE' || selectedProject.pm_readiness === 'WO_DRAFT') && (
                  <button
                    onClick={() => handleUnassign(selectedProject)}
                    className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                  >
                    Unassign
                  </button>
                )}
              </div>
            </div>

            {/* SKU Details & Asset Tracking */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-700">
                  SKU Details & Asset Tracking
                  {loadingDetails && (
                    <span className="ml-2 text-xs text-gray-400">Loading...</span>
                  )}
                </h3>
                <div className="flex items-center gap-2 text-xs">
                  {skuTrackingSummary.allArtworkReceived ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">
                      All artwork received
                    </span>
                  ) : skuTracking.length > 0 ? (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                      Artwork pending
                    </span>
                  ) : null}
                </div>
              </div>

              {skuDisplayData.length > 0 ? (
                <div className="space-y-3">
                  {skuDisplayData.map((item, idx) => {
                    const isUpdating = updatingSku === item.sku;

                    return (
                      <div
                        key={`${selectedProject.quote_number}-${item.sku}-${idx}`}
                        className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        {/* SKU Header */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">
                              {item.sku}
                            </p>
                            <p className="text-gray-600 text-sm mt-0.5 line-clamp-2">
                              {item.description || "No description"}
                            </p>
                            {item.productType && (
                              <p className="text-xs text-purple-600 mt-1">
                                Type: {item.productType}
                              </p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <p className="text-sm font-medium text-gray-700">
                              {item.quantity || 0} qty
                            </p>
                          </div>
                        </div>

                        {/* Tracking Controls */}
                        <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                          {/* Artwork Status */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Artwork:</span>
                            <button
                              onClick={() => {
                                const newValue = item.artwork === "received" ? "missing" : "received";
                                updateSkuStatus(item.sku, "artwork", newValue);
                              }}
                              disabled={isUpdating}
                              className={`px-2 py-1 text-xs rounded transition-colors ${
                                item.artwork === "received"
                                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                                  : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                              } ${isUpdating ? "opacity-50 cursor-wait" : ""}`}
                            >
                              {isUpdating ? "..." : item.artwork === "received" ? "Received" : "Missing"}
                            </button>
                          </div>

                          {/* Structure Status */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Structure:</span>
                            <select
                              value={item.structure || "not_needed"}
                              onChange={(e) => updateSkuStatus(item.sku, "structure", e.target.value)}
                              disabled={isUpdating}
                              className={`px-2 py-1 text-xs rounded border transition-colors ${
                                item.structure === "received"
                                  ? "bg-green-50 border-green-200 text-green-700"
                                  : item.structure === "needed"
                                    ? "bg-yellow-50 border-yellow-200 text-yellow-700"
                                    : "bg-gray-50 border-gray-200 text-gray-600"
                              } ${isUpdating ? "opacity-50 cursor-wait" : ""}`}
                            >
                              <option value="not_needed">Not needed</option>
                              <option value="needed">Needed</option>
                              <option value="received">Received</option>
                            </select>
                          </div>

                          {/* # of POA - with proper increment handling */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500"># POA:</span>
                            <input
                              type="number"
                              min="1"
                              max="99"
                              step="1"
                              defaultValue={item.poaCount ?? 1}
                              onBlur={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                const clamped = Math.max(1, Math.min(99, val));
                                if (clamped !== (item.poaCount ?? 1)) {
                                  updateSkuStatus(item.sku, "poaCount", clamped);
                                }
                                e.target.value = clamped;
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                              disabled={isUpdating}
                              className={`w-16 px-2 py-1 text-xs rounded border border-gray-200 text-center ${
                                isUpdating ? "opacity-50 cursor-wait" : ""
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : loadingDetails ? (
                <p className="text-sm text-gray-400">Loading SKUs...</p>
              ) : (
                <p className="text-sm text-gray-400">No SKU data available for this project</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* SharePoint Folder Modal */}
      <SharePointFolderModal
        isOpen={showFolderModal}
        onClose={() => {
          setShowFolderModal(false);
          setPendingWOProject(null);
        }}
        onConfirm={handleConfirmGenerateWO}
        projectName={pendingWOProject?.description || pendingWOProject?.customer_name || ""}
        quoteNumber={pendingWOProject?.quote_number || ""}
        existingFolder={pendingWOProject?.sharepoint_folder_url || ""}
      />
    </div>
  );
}
